import React from 'react';
import Modal from '../ui/Modal';
import { formatQuotaInfo, getQuotaTypeInfo, getSeverityStyle } from '../../utils/licenseErrorHandler';

const QuotaExceededModal = ({ isOpen, onClose, licenseError }) => {
    if (!licenseError || !isOpen) return null;

    const quotaInfo = formatQuotaInfo(licenseError.quota);
    const typeInfo = getQuotaTypeInfo(licenseError.quotaType);
    const severityStyle = getSeverityStyle('error');

    const handleUpgrade = () => {
        if (licenseError.upgradeUrl) {
            window.open(licenseError.upgradeUrl, '_blank');
        }
        onClose();
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toString();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center mb-6">
                    <div className={`w-12 h-12 rounded-full ${severityStyle.bgColor} ${severityStyle.borderColor} border-2 flex items-center justify-center mr-4`}>
                        <span className="text-2xl">{typeInfo.icon}</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {typeInfo.name} Quota Exceeded
                        </h3>
                        <p className="text-sm text-gray-600">
                            You've reached your {typeInfo.name.toLowerCase()} limit
                        </p>
                    </div>
                </div>

                {/* Quota Display */}
                <div className={`${severityStyle.bgColor} ${severityStyle.borderColor} border rounded-lg p-4 mb-6`}>
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Current Usage</span>
                        <span className={`text-sm font-semibold ${severityStyle.textColor}`}>
                            {quotaInfo?.percentage}% used
                        </span>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                        <div
                            className="bg-red-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(quotaInfo?.progressPercentage || 0, 100)}%` }}
                        />
                    </div>
                    
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                            {formatNumber(quotaInfo?.used || 0)} {typeInfo.unit} used
                        </span>
                        <span className="text-gray-600">
                            {formatNumber(quotaInfo?.limit || 0)} {typeInfo.unit} limit
                        </span>
                    </div>
                    
                    {quotaInfo?.requested > 0 && (
                        <div className="mt-2 p-2 bg-white rounded border border-red-200">
                            <p className="text-sm text-red-700">
                                Attempted to add {quotaInfo.requested} more {typeInfo.unit}, 
                                but only {quotaInfo.available} remaining.
                            </p>
                        </div>
                    )}
                </div>

                {/* All Quotas Overview */}
                {licenseError._quotas && (
                    <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">All Quota Usage</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {Object.entries(licenseError._quotas).map(([quotaType, quota]) => {
                                const qInfo = getQuotaTypeInfo(quotaType);
                                const qData = formatQuotaInfo(quota);
                                
                                return (
                                    <div
                                        key={quotaType}
                                        className={`p-3 rounded-md border ${
                                            quota.isExceeded 
                                                ? 'bg-red-50 border-red-200' 
                                                : quota.isWarning 
                                                    ? 'bg-yellow-50 border-yellow-200'
                                                    : 'bg-green-50 border-green-200'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">
                                                {qInfo.icon} {qInfo.name}
                                            </span>
                                            <span className={`text-xs font-semibold ${
                                                quota.isExceeded ? 'text-red-600' :
                                                quota.isWarning ? 'text-yellow-600' : 'text-green-600'
                                            }`}>
                                                {qData?.percentage}%
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            {formatNumber(quota.used)}/{formatNumber(quota.limit)} {qInfo.unit}
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                            <div
                                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                                    quota.isExceeded ? 'bg-red-500' :
                                                    quota.isWarning ? 'bg-yellow-500' : 'bg-green-500'
                                                }`}
                                                style={{ width: `${Math.min(qData?.progressPercentage || 0, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Plan Information */}
                {licenseError.plan && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Current Plan</p>
                                <p className="text-lg font-semibold text-gray-900 capitalize">
                                    {licenseError.plan}
                                </p>
                            </div>
                            {licenseError._license?.blockingLevel > 0 && (
                                <div className="text-right">
                                    <p className="text-xs text-gray-600">Blocking Level</p>
                                    <p className="text-sm font-semibold text-orange-600">
                                        Level {licenseError._license.blockingLevel}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Help Message */}
                {licenseError.help && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm text-blue-800">{licenseError.help}</p>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Continue with Current Plan
                    </button>
                    
                    {licenseError.upgradeUrl && (
                        <button
                            onClick={handleUpgrade}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${severityStyle.buttonColor}`}
                        >
                            {licenseError.action || 'Upgrade Plan'}
                        </button>
                    )}
                </div>

                {/* Timestamp */}
                {licenseError.timestamp && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500 text-center">
                            Quota exceeded at {new Date(licenseError.timestamp).toLocaleString()}
                        </p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default QuotaExceededModal;